// components/secure/EscrowDivisionPanel.js — Escrow Compliance → Signatories, Division
// notices and record incidents.
//
// Three escrow duties that had working endpoints and no screen:
//   * who may withdraw from each escrow account — adding or removing one starts the
//     10-day clock to tell the Division (68 IAC 1450.750(m));
//   * the Division notifications themselves: due, overdue, and recording how each was sent;
//   * escrow records lost, stolen or destroyed — reported within 48 hours of discovery
//     (1450.755) and reconstructed.
// The managing broker acts; everyone with access to this page can see the state.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';

const input = 'border border-slate-300 rounded px-2 py-1 text-sm';
const btn = 'px-3 py-1.5 text-sm bg-slate-800 text-white rounded disabled:bg-indigo-300';
const EVENTS = ['account_opened', 'account_closed', 'depository_changed', 'account_number_changed', 'signatory_added', 'signatory_removed', 'initial_licensure', 'other'];
const METHODS = ['idfpr_portal', 'email', 'mail', 'in_person', 'other'];
const nice = (s) => String(s || '').replace(/_/g, ' ');
const today = () => new Date().toISOString().slice(0, 10);

export default function EscrowDivisionPanel() {
    const api = useApiClient();
    const broker = !!getCurrentUser()?.isManagingBroker;
    const [accounts, setAccounts] = useState([]);
    const [acct, setAcct] = useState('');
    const [signers, setSigners] = useState([]);
    const [notes, setNotes] = useState(null);
    const [incidents, setIncidents] = useState(null);
    const [newSigner, setNewSigner] = useState({ person_name: '', role: '', authorised_from: today() });
    const [sending, setSending] = useState(null);
    const [raising, setRaising] = useState(null);
    const [incident, setIncident] = useState(null);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [a, n] = await Promise.all([api.get('/api/escrow/accounts'), api.get('/api/escrow/division-notifications')]);
            setAccounts(a || []); setNotes(n);
            if (!acct && a?.length) setAcct(String(a[0].account_id));
            if (broker) setIncidents(await api.get('/api/escrow/record-incidents'));
        } catch (e) { if (e.status !== 403) setError(e.message); }
    }, [api, acct, broker]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!acct) return;
        api.get(`/api/escrow/accounts/${acct}/signatories`).then(setSigners).catch((e) => setError(e.message));
    }, [api, acct]);

    const act = async (fn) => {
        setBusy(true); setError(''); setMsg('');
        try {
            const r = await fn();
            setMsg(r?.note || r?.message || 'Saved.');
            await load();
            if (acct) setSigners(await api.get(`/api/escrow/accounts/${acct}/signatories`));
            return true;
        } catch (e) { setError(e.message); return false; } finally { setBusy(false); }
    };

    const addSigner = () => act(async () => {
        const r = await api.post(`/api/escrow/accounts/${acct}/signatories`, newSigner);
        setNewSigner({ person_name: '', role: '', authorised_from: today() });
        return r;
    });
    const removeSigner = (s) => {
        const reason = window.prompt(`Why is ${s.person_name} no longer authorised to withdraw? (recorded, and a Division notice is raised)`);
        if (!reason) return;
        act(() => api.post(`/api/escrow/signatories/${s.signatory_id}/remove`, { reason }));
    };

    if (!notes) return error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null;
    const open = [...(notes.overdue || []), ...(notes.due || [])];

    return (
        <div className="bg-white rounded-lg shadow p-5 mb-6 space-y-5">
            <div>
                <h2 className="text-lg font-semibold text-slate-800">Signatories, Division notices and record incidents</h2>
                <p className="text-xs text-slate-500">{notes.authority}</p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {msg && <p className="text-sm text-green-700">{msg}</p>}

            {/* Division notifications */}
            <div>
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-700">Division notifications {open.length > 0 && <span className={`ml-1 text-xs px-2 py-0.5 rounded ${notes.overdue?.length ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>{open.length} open{notes.overdue?.length ? `, ${notes.overdue.length} overdue` : ''}</span>}</h3>
                    {broker && <button className="text-sm text-indigo-600 hover:underline" onClick={() => setRaising({ event_type: 'depository_changed', event_on: today(), account_id: acct, detail: '' })}>+ Raise one</button>}
                </div>
                {open.length === 0 ? <p className="text-sm text-slate-500 mt-1">Nothing owed to the Division.</p> : (
                    <ul className="divide-y text-sm mt-1">
                        {open.map((n) => (
                            <li key={n.notification_id} className="py-1.5 flex justify-between gap-2 flex-wrap">
                                <span><strong>{nice(n.event_type)}</strong> {n.account_name ? `· ${n.account_name}` : ''} — {n.detail} <span className={n.days_out < 0 ? 'text-red-700 font-semibold' : 'text-slate-500'}>due {n.due_by}{n.days_out < 0 ? ' (overdue)' : ''}</span></span>
                                {broker && <button className="text-indigo-600 hover:underline" onClick={() => setSending({ id: n.notification_id, sent_method: 'idfpr_portal', sent_on: today(), sent_reference: '', notes: '' })}>Record as sent</button>}
                            </li>
                        ))}
                    </ul>
                )}
                {sending && (
                    <div className="mt-2 p-3 bg-slate-50 rounded flex flex-wrap gap-2 items-end text-sm">
                        <label>How<select className={`${input} block`} value={sending.sent_method} onChange={(e) => setSending({ ...sending, sent_method: e.target.value })}>{METHODS.map((m) => <option key={m} value={m}>{nice(m)}</option>)}</select></label>
                        <label>On<input type="date" className={`${input} block`} value={sending.sent_on} onChange={(e) => setSending({ ...sending, sent_on: e.target.value })} /></label>
                        <label className="flex-1 min-w-[180px]">Reference (confirmation no., email subject)<input className={`${input} block w-full`} value={sending.sent_reference} onChange={(e) => setSending({ ...sending, sent_reference: e.target.value })} /></label>
                        <button className={btn} disabled={busy} onClick={async () => { if (await act(() => api.post(`/api/escrow/division-notifications/${sending.id}/sent`, sending))) setSending(null); }}>Save</button>
                        <button className="text-sm" onClick={() => setSending(null)}>Cancel</button>
                    </div>
                )}
                {raising && (
                    <div className="mt-2 p-3 bg-slate-50 rounded flex flex-wrap gap-2 items-end text-sm">
                        <label>Change<select className={`${input} block`} value={raising.event_type} onChange={(e) => setRaising({ ...raising, event_type: e.target.value })}>{EVENTS.map((m) => <option key={m} value={m}>{nice(m)}</option>)}</select></label>
                        <label>Account<select className={`${input} block`} value={raising.account_id} onChange={(e) => setRaising({ ...raising, account_id: e.target.value })}><option value="">—</option>{accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}</select></label>
                        <label>Happened on<input type="date" className={`${input} block`} value={raising.event_on} onChange={(e) => setRaising({ ...raising, event_on: e.target.value })} /></label>
                        <label className="flex-1 min-w-[180px]">Detail<input className={`${input} block w-full`} value={raising.detail} onChange={(e) => setRaising({ ...raising, detail: e.target.value })} /></label>
                        <button className={btn} disabled={busy} onClick={async () => { if (await act(() => api.post('/api/escrow/division-notifications', raising))) setRaising(null); }}>Raise</button>
                        <button className="text-sm" onClick={() => setRaising(null)}>Cancel</button>
                    </div>
                )}
            </div>

            {/* Signatories */}
            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-slate-700">Who may withdraw</h3>
                    <select className={input} value={acct} onChange={(e) => setAcct(e.target.value)}>
                        {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name} — {a.bank_name}</option>)}
                    </select>
                </div>
                {signers.length === 0 ? <p className="text-sm text-amber-700 mt-1">No signatories recorded for this account.</p> : (
                    <ul className="divide-y text-sm mt-1">
                        {signers.map((s) => (
                            <li key={s.signatory_id} className="py-1.5 flex justify-between gap-2">
                                <span className={s.is_current ? '' : 'text-slate-400 line-through'}>{s.person_name}{s.role ? ` (${s.role})` : ''} · from {s.authorised_from}{s.authorised_to ? ` to ${s.authorised_to} — ${s.removal_reason || ''}` : ''}</span>
                                {broker && s.is_current && <button className="text-red-600 hover:underline" onClick={() => removeSigner(s)}>End authority</button>}
                            </li>
                        ))}
                    </ul>
                )}
                {broker && accounts.length > 0 && (
                    <div className="flex flex-wrap gap-2 items-center mt-2 text-sm">
                        <input className={input} placeholder="Name" value={newSigner.person_name} onChange={(e) => setNewSigner({ ...newSigner, person_name: e.target.value })} />
                        <input className={input} placeholder="Role (e.g. Managing broker)" value={newSigner.role} onChange={(e) => setNewSigner({ ...newSigner, role: e.target.value })} />
                        <input type="date" className={input} value={newSigner.authorised_from} onChange={(e) => setNewSigner({ ...newSigner, authorised_from: e.target.value })} />
                        <button className={btn} disabled={busy || !newSigner.person_name.trim()} onClick={addSigner}>Add signatory</button>
                    </div>
                )}
            </div>

            {/* Record incidents */}
            {broker && incidents && (
                <div>
                    <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-slate-700">Lost, stolen or destroyed records {incidents.unreported?.length > 0 && <span className="ml-1 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">{incidents.unreported.length} not yet reported</span>}</h3>
                        <button className="text-sm text-indigo-600 hover:underline" onClick={() => setIncident({ incident_type: 'lost', records_affected: '', discovered_on: new Date().toISOString().slice(0, 16), account_id: acct, period_affected: '', notes: '' })}>+ Record an incident</button>
                    </div>
                    {incidents.incidents.length === 0 ? <p className="text-sm text-slate-500 mt-1">None recorded.</p> : (
                        <ul className="divide-y text-sm mt-1">
                            {incidents.incidents.map((i) => (
                                <li key={i.incident_id} className="py-1.5 flex justify-between gap-2 flex-wrap">
                                    <span><strong>{i.incident_type}</strong> · {i.records_affected} · reconstruction {nice(i.reconstruction_status)} — {i.reported_on ? `reported ${String(i.reported_on).slice(0, 10)} to ${i.reported_to || '—'}` : <span className={i.hours_remaining < 0 ? 'text-red-700 font-semibold' : 'text-amber-700'}>report due {new Date(i.report_due_by).toLocaleString()}</span>}</span>
                                    <span className="space-x-2">
                                        {!i.reported_on && <button className="text-indigo-600 hover:underline" onClick={() => {
                                            const to = window.prompt('Reported to (e.g. IDFPR Enforcement, by email to …)'); if (!to) return;
                                            act(() => api.put(`/api/escrow/record-incidents/${i.incident_id}`, { reported_on: today(), reported_to: to }));
                                        }}>Mark reported</button>}
                                        {i.reconstruction_status !== 'complete' && <button className="text-indigo-600 hover:underline" onClick={() => act(() => api.put(`/api/escrow/record-incidents/${i.incident_id}`, { reconstruction_status: 'complete', reconstructed_on: today() }))}>Reconstruction complete</button>}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {incident && (
                        <div className="mt-2 p-3 bg-slate-50 rounded flex flex-wrap gap-2 items-end text-sm">
                            <label>What happened<select className={`${input} block`} value={incident.incident_type} onChange={(e) => setIncident({ ...incident, incident_type: e.target.value })}>{['lost', 'stolen', 'destroyed', 'corrupted', 'other'].map((m) => <option key={m}>{m}</option>)}</select></label>
                            <label className="flex-1 min-w-[180px]">Which records<input className={`${input} block w-full`} value={incident.records_affected} onChange={(e) => setIncident({ ...incident, records_affected: e.target.value })} /></label>
                            <label>Discovered<input type="datetime-local" className={`${input} block`} value={incident.discovered_on} onChange={(e) => setIncident({ ...incident, discovered_on: e.target.value })} /></label>
                            <button className={btn} disabled={busy || !incident.records_affected.trim()} onClick={async () => { if (await act(() => api.post('/api/escrow/record-incidents', incident))) setIncident(null); }}>Record</button>
                            <button className="text-sm" onClick={() => setIncident(null)}>Cancel</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
